import { Autocomplete, TextField } from '@mui/material';
import {
  ClinicOption,
  buildClinicOptions,
  setClinicFilterInSearch,
} from 'controllers/review/clinic-filter';
import ClinicsServiceClient from 'node-api/clinics/clinics.client';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MixpanelWrapperInstance } from 'services/mixpanel/mixpanel-wrapper';
import { EventNames } from 'services/mixpanel/provider-app-mixpanel-events';
import { SelectableElement } from 'services/mixpanel/shared-mixpanel-service';
import { PatientContext } from 'state/contexts/patient';
import { ActionTypes } from 'state/reducers/patient';
import { debug } from 'utils/helpers';

// Clinic names resolved for lists that don't send `clinic_name` (RL2), shared across mounts
const clinicNamesCache: Record<number, string> = {};

type Props = {
  reviewListLabel?: string;
};

const ReviewListClinicFilter: FC<Props> = ({ reviewListLabel }) => {
  const { patientState, patientDispatch } = useContext(PatientContext);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const [fallbackNames, setFallbackNames] = useState<Record<number, string>>(clinicNamesCache);

  const members = patientState.data;

  const missingNameClinicIds = useMemo(() => {
    const ids = members
      .filter((member) => member.clinic_id && !member.clinic_name)
      .map((member) => member.clinic_id as number)
      .filter((clinicId) => !(clinicId in clinicNamesCache));

    return Array.from(new Set(ids)).sort((a, b) => a - b);
  }, [members]);
  const missingNameClinicIdsKey = missingNameClinicIds.join(',');

  useEffect(() => {
    if (!missingNameClinicIds.length) {
      return undefined;
    }

    let cancelled = false;
    const clinicsServiceClient = new ClinicsServiceClient();

    const fetchClinicNames = async () => {
      const results = await Promise.allSettled(
        missingNameClinicIds.map((clinicId) => clinicsServiceClient.getClinic(clinicId))
      );

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          clinicNamesCache[result.value.id] = result.value.name;
        } else {
          debug(`${result.reason}`);
        }
      });

      if (!cancelled) {
        setFallbackNames({ ...clinicNamesCache });
      }
    };

    fetchClinicNames();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingNameClinicIdsKey]);

  const options = useMemo(
    () => buildClinicOptions(members, fallbackNames),
    [members, fallbackNames]
  );

  // A single clinic means there is nothing to filter
  if (options.length < 2) {
    return null;
  }

  const selectedOption = options.find(({ clinicId }) => clinicId === patientState.clinicFilter);

  const handleChange = (_: unknown, option: ClinicOption | null) => {
    const clinicId = option?.clinicId ?? null;

    patientDispatch({ type: ActionTypes.SET_CLINIC_FILTER, payload: { clinicId } });
    history.replace(`${pathname}${setClinicFilterInSearch(search, clinicId)}`);

    MixpanelWrapperInstance.track(EventNames.ReviewListsFilterByClinic, {
      targetLabel: 'clinic filter',
      targetType: SelectableElement.DROPDOWN,
      targetLocation: 'members to review',
      List: reviewListLabel,
      'clinic-id': clinicId,
    });
  };

  return (
    <Autocomplete
      size='small'
      sx={{ width: 280 }}
      options={options}
      value={selectedOption ?? null}
      onChange={handleChange}
      getOptionLabel={({ name, count }) => `${name} (${count})`}
      isOptionEqualToValue={(option, value) => option.clinicId === value.clinicId}
      renderInput={(params) => (
        // MUI hides the placeholder behind an unfocused label, so the label carries "All clinics"
        <TextField
          {...params}
          label={selectedOption ? 'Clinic' : `All clinics (${members.length})`}
        />
      )}
    />
  );
};

export default ReviewListClinicFilter;
